import React, {useRef} from 'react';
import {Form, Button} from 'react-bootstrap';
import {PencilSquare, Trash, Image as ImageIcon, PlusCircle} from 'react-bootstrap-icons'

const svgMap = {
    ball: '/assets/images/ball.svg',
    balloon: '/assets/images/balloon.svg',
    canoe: '/assets/images/canoe.svg',
    duck: '/assets/images/duck.svg',
    hotDog: '/assets/images/hot-dog.svg',
    microphone: '/assets/images/microphone.svg',
    popcorn: '/assets/images/popcorn.svg',
    strawberry: '/assets/images/strawberry.svg'
};

interface ImagePickerProps {
    image: string;
    setImage: (image: string) => void;
}

const ImagePicker: React.FC<ImagePickerProps> = ({image, setImage}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const generateSampleImage = (type:
                                     'ball' | 'balloon' | 'canoe' | 'duck' | 'hotDog' |
                                     'microphone' | 'popcorn' | 'strawberry'
    ) => {
        console.log(`[DEBUG_LOG] ImagePicker.generateSampleImage - Generating ${type}`);

        const svgUrl = svgMap[type];

        const img = new window.Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = 150;
            canvas.height = 150;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(img, 0, 0, 150, 150);
                setImage(canvas.toDataURL('image/png'));
            }
        };
        img.src = svgUrl;
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log("[DEBUG_LOG] ImagePicker.handleImageChange - Processing image...");
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new window.Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const maxWidth = 150;
                    const scale = maxWidth / img.width;
                    canvas.width = maxWidth;
                    canvas.height = img.height * scale;

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        const dataUrl = canvas.toDataURL('image/png');
                        setImage(dataUrl);
                        console.log("[DEBUG_LOG] ImagePicker.handleImageChange - Image resized and processed");
                    }
                };
                img.src = event.target?.result as string;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log("[DEBUG_LOG] ImagePicker.handleRemoveImage - Removing image");
        setImage('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const triggerFileInput = () => {
        console.log("[DEBUG_LOG] ImagePicker.triggerFileInput - Triggering file input");
        fileInputRef.current?.click();
    };

    return (
        <Form.Group className="mb-4">
            <Form.Label className="fw-bold small text-uppercase" style={{ color: 'var(--bingo-text-muted)' }}>Imagem Central (opcional)</Form.Label>
            <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                style={{display: 'none'}}
            />
            <div className="d-flex flex-column gap-2">
                <div className="image-picker-container" onClick={triggerFileInput}>
                    {image ? (
                        <>
                            <img src={image} alt="Preview" className="image-picker-preview"/>
                            <button
                                type="button"
                                className="image-picker-remove-btn"
                                onClick={handleRemoveImage}
                                title="Remover imagem"
                            >
                                <Trash size={18}/>
                            </button>
                            <div className="image-picker-edit-btn">
                                <PencilSquare size={18}/>
                            </div>
                        </>
                    ) : (
                        <div className="image-picker-placeholder">
                            <ImageIcon size={32} className="mb-2"/>
                            <span>Clique para selecionar</span>
                            <PlusCircle size={16} className="mt-1"/>
                        </div>
                    )}
                </div>

                <div className="d-flex flex-wrap gap-2 mt-2">
                    {Object.entries(svgMap).map(([key, svgUrl]) => (
                        <Button
                            key={key}
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => generateSampleImage(key as keyof typeof svgMap)}
                        >
                            <img src={svgUrl} alt={key} style={{width: '25px', height: '25px', marginRight: '4px'}}/>
                        </Button>
                    ))}
                </div>
            </div>
        </Form.Group>
    );
};

export default ImagePicker;
